import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';

import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { Observable, filter } from 'rxjs';

import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../base/store/state';
import { PickType } from '../../../core/picks/pick-models';
import { IDigitalBetslipMessages } from '../../../digital/services/betslip-message-provider.service';
import { BETSLIP_LINE_SWITCHER_INJECTION_TOKEN } from '../../../integration/services/line-switcher-pick-handler.service';
import { LineSwitcherActions } from '../actions';
import { ISportPick } from '../models';
import { lineSwitcherStateSelector } from '../selector';
import { LineSwitcherPickHandlerService } from '../services/line-switcher-pick-handler.service';
import { ILineSwitcherState } from '../state';
import { LineSwitcherSwapOptionsService } from './../services/line-switcher-swap-options.service';

@Component({
    templateUrl: './line-switcher-dialog-container.html',
    styleUrls: ['./line-switcher-dialog-container.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: BETSLIP_LINE_SWITCHER_INJECTION_TOKEN, useClass: LineSwitcherPickHandlerService }],
    standalone: false,
})
export class LineSwitcherDialogContainerComponent implements OnInit, OnDestroy {
    lineSwitcherState$: Observable<ILineSwitcherState>;

    @Input() event: EventModel; // Shared between all pick types
    @Input() optionGroup: EventOptionGroup;
    @Input() pick: ISportPick;

    swapButtonText: string;
    errorMessage: string;
    pickType: typeof PickType = PickType;
    tracking = trackingConstants.LINE_SWITCHER_TRACKING;
    readonly messages: {
        swapYourSelection: string;
    };

    private pushSubscription?: EventSubscription<EventModel>;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private router: Router,
        private subscriptionService: DetailedEventSubscriptionService,
        private userService: UserService,
        private store: Store<IBetslipRootState>,
        private changeDetector: ChangeDetectorRef,
        private lineSwitcherSwapOptionsService: LineSwitcherSwapOptionsService,
    ) {
        this.lineSwitcherState$ = this.store.select(lineSwitcherStateSelector);
        const messages = messageProvider.getMessages() as IDigitalBetslipMessages;
        this.messages = {
            swapYourSelection: messages.lineSwitcher.SwapYourSelection,
        };
    }

    ngOnInit(): void {
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationStart && e.navigationTrigger === 'popstate'),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.close();
                this.changeDetector.markForCheck();
            });

        this.subscribeToPush();

        this.userService.onUserLogin$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.close();
            this.changeDetector.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.pushSubscription?.unsubscribe();
    }

    hasError(state: ILineSwitcherState): boolean {
        return !!state.errorState.message;
    }

    isLineSwitcherPick(state: ILineSwitcherState): boolean {
        const type = state.swapPickState.oldPickId.getPickType();

        return type === this.pickType.V1Pick || type === this.pickType.V2OptionMarketPick;
    }

    private close = (): void => {
        this.store.dispatch(LineSwitcherActions.cancelLineSwitcher());
    };

    private handlePushUpdate = (): void => {
        const optionGroup = this.lineSwitcherSwapOptionsService.getOptionGroups(this.event, this.pick.optionGroupId);

        if (optionGroup) {
            this.optionGroup = Object.assign(new EventOptionGroup(), optionGroup);
            this.store.dispatch(LineSwitcherActions.updateOptionGroup({ payload: optionGroup }));
            this.store.dispatch(LineSwitcherActions.handlePushUpdate());
        }
    };

    private subscribeToPush(): void {
        this.pushSubscription?.unsubscribe();

        if (this.event) {
            this.pushSubscription = this.subscriptionService.subscribe([
                {
                    event: this.event,
                    callback: () => this.handlePushUpdate(),
                },
            ]);
        }
    }
}
